import { cn } from '../cn.js';
import { useDrawIn } from './internal/useDrawIn.js';
import { ChartTooltip } from './internal/ChartTooltip.js';
import { useChartHover } from './internal/useChartHover.js';

export interface CalibrationCurveBand {
  id: string;
  /** Rendered under the band's dot cluster and in the legend row, e.g. "Meets Expectations". */
  label: string;
  count: number;
  percent: number;
  /** A token reference, e.g. "var(--success)" — one per band, sets the dot fill, the background
   *  tint behind its slot, and the legend swatch. */
  color: string;
}

export interface CalibrationCurveChartProps {
  bands: CalibrationCurveBand[];
  /** The server-sampled normal-curve overlay (`RatingDistribution.normalCurve`), already numeric —
   *  the panel converts the wire's decimal strings before passing them in here, same convention
   *  `RatingDistributionPanel` already used for `LineChart`. Omitted draws the dot clusters with no
   *  reference curve beneath them (e.g. too few sampled points to bother). */
  curve?: { x: number; y: number }[];
  /** A vertical marker for the cohort mean — omitted draws no marker. */
  mean?: number;
  meanLabel?: string;
  height?: number;
  /** Caps how many individual dots one band draws before collapsing to a single "+N" marker — a
   *  cohort of 40 in one band would otherwise pack forty 6px circles into one slot and read as
   *  noise rather than a count. */
  maxDotsPerBand?: number;
  legend?: boolean;
  /** The dashed-line/dot symbol key above the plot, explaining what the two marks mean — omit for
   *  a denser layout when the caller already explains the symbols elsewhere. */
  topLegend?: boolean;
  curveLabel?: string;
  dotLabel?: string;
  /** Y-axis caption. A HEADCOUNT axis, not a percent one — see the curve-scaling comment in the
   *  component body for why: it's the one unit real per-band counts and the server's sampled curve
   *  both share, so the two can be plotted against the same gridlines honestly. */
  yAxisLabel?: string;
  className?: string;
  ariaLabel?: string;
}

const PADDING = { top: 20, right: 20, bottom: 34, left: 40 };
const VIEW_WIDTH = 600;
const DOT_RADIUS = 4;
const DOT_SPACING = 11;
const DEFAULT_MAX_DOTS = 30;
const DRAW_IN_DURATION = '480ms';
/** Fixed 4-step axis (5 gridlines: 0, ¼, ½, ¾, and the rounded max) — same fixed-tick-count
 *  convention as a typical bell-curve reference chart, rather than a scale that reflows tick
 *  spacing per cohort. */
const Y_AXIS_STEPS = 4;

/**
 * The "Calibration Curve" chart — a dot-per-person cluster per rating band, positioned along a
 * shared score axis, with the cohort's server-sampled normal-distribution curve drawn behind it as
 * a reference. Distinct from `BarChart`'s per-band histogram: a bar communicates "how much"; a
 * cluster of individually-countable dots communicates "how many people, banded", which is the
 * calibration-meeting reading this chart is for (spotting how a cohort clusters against the
 * expected bell shape, not just each band's share).
 *
 * Each band occupies an equal-width slot along the x-axis (real bands ARE equal-width by
 * construction — see `defaultRatingBands` in `packages/shared`), so a band's dot cluster lines up
 * under the matching stretch of the reference curve without the chart needing each band's own
 * numeric score range from the API (the contract intentionally only carries `{band, count,
 * percent}` — see `ratingDistributionBucketSchema`'s header on why band ranges are HR-configurable
 * and not fixed). That is also why this chart never prints a numeric score range under a band
 * label: there isn't one to print without guessing at HR-configurable boundaries.
 *
 * Hand-rolled SVG, same reasoning as `BarChart`/`LineChart`: no charting dependency in this repo for
 * a handful of bands per screen. `viewBox`-scaled to a fixed internal coordinate space and rendered
 * at 100% width, so it reflows with its container instead of needing a measured pixel width.
 */
export function CalibrationCurveChart({
  bands,
  curve = [],
  mean,
  meanLabel = 'Mean',
  height = 220,
  maxDotsPerBand = DEFAULT_MAX_DOTS,
  legend = true,
  topLegend = true,
  curveLabel = 'Cohort Distribution (Bell Curve)',
  dotLabel = 'This Cohort',
  yAxisLabel = 'Employees',
  className,
  ariaLabel = 'Calibration curve — rating distribution by band',
}: CalibrationCurveChartProps) {
  const [drawInRef, mounted] = useDrawIn<HTMLDivElement>();
  const hover = useChartHover(bands.length);

  if (bands.length === 0) return null;

  const plotLeft = PADDING.left;
  const plotRight = VIEW_WIDTH - PADDING.right;
  const plotWidth = plotRight - plotLeft;
  const baseline = height - PADDING.bottom;
  const curveTop = PADDING.top;
  const curveHeight = baseline - curveTop;

  const curveXs = curve.map((p) => p.x);
  const domainMin = curveXs.length > 0 ? Math.min(...curveXs) : 0;
  const domainMax = curveXs.length > 0 ? Math.max(...curveXs) : bands.length;
  const domainSpan = domainMax - domainMin || 1;
  const xForScore = (score: number) => plotLeft + ((score - domainMin) / domainSpan) * plotWidth;

  const slotWidth = plotWidth / bands.length;
  const slotLeft = (i: number) => plotLeft + slotWidth * i;
  const slotCenter = (i: number) => plotLeft + slotWidth * (i + 0.5);

  // `normalCurve.y` is the server's PDF sample scaled to expected headcount per FINE step (see
  // `sampleNormalCurve` in performance-insights.ts: "scaled so the curve overlays a histogram of n
  // observations across `steps` buckets" — steps being `curve.length - 1`, much narrower than one
  // of the 5 rating bands). Plotting it against a headcount axis sized off the wide BAND counts
  // without correcting for that would make the curve read as nearly flat — every fine step holds a
  // small slice of one band's population. Multiplying by (steps per band) converts "expected count
  // in one fine step" to "expected count across one band-width slice", which IS the same quantity
  // `band.count` is — a real, unit-matched calibration, not two independently-normalized scales
  // sharing a pixel budget.
  const stepsPerBand = curve.length > 1 ? (curve.length - 1) / bands.length : 1;
  const curveAsHeadcount = curve.map((p) => ({ x: p.x, y: p.y * stepsPerBand }));

  const axisMax = Math.max(
    5,
    Math.ceil(
      (Math.max(...bands.map((b) => b.count), ...curveAsHeadcount.map((p) => p.y), 1) * 1.15) / 5,
    ) * 5,
  );
  const yTicks = Array.from({ length: Y_AXIS_STEPS + 1 }, (_, i) => (axisMax / Y_AXIS_STEPS) * i);
  const yForAxis = (value: number) => baseline - (value / axisMax) * curveHeight;
  const curvePath = curveAsHeadcount.map((p) => `${xForScore(p.x)},${yForAxis(p.y)}`).join(' ');

  const activeBand = hover.activeIndex != null ? bands[hover.activeIndex] : undefined;

  return (
    <div ref={drawInRef} className={cn('w-full', className)}>
      {topLegend && (
        <div className="mb-2 flex flex-wrap items-center justify-center gap-x-5 gap-y-1 rounded-lg border border-border bg-bg-subtle px-3 py-1.5 text-xs text-fg-muted">
          <span className="flex items-center gap-1.5">
            <svg width="18" height="8" aria-hidden="true">
              <line
                x1={0}
                y1={4}
                x2={18}
                y2={4}
                stroke="var(--fg-subtle)"
                strokeWidth={1.5}
                strokeDasharray="3 2"
              />
            </svg>
            {curveLabel}
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="size-2.5 rounded-full"
              style={{ backgroundColor: 'var(--accent)' }}
              aria-hidden="true"
            />
            {dotLabel}
          </span>
        </div>
      )}

      <div className="relative w-full">
        <svg
          viewBox={`0 0 ${VIEW_WIDTH} ${height}`}
          width="100%"
          height={height}
          role="img"
          aria-label={ariaLabel}
          preserveAspectRatio="none"
          {...hover.rootProps}
        >
          {/* Per-band background tint, drawn first so every other mark sits on top of it. */}
          {bands.map((band, i) => (
            <rect
              key={`${band.id}-bg`}
              x={slotLeft(i)}
              y={curveTop}
              width={slotWidth}
              height={baseline - curveTop}
              fill={band.color}
              opacity={hover.activeIndex == null || hover.activeIndex === i ? 0.08 : 0.04}
            />
          ))}
          {/* Dashed dividers between band slots. */}
          {bands.slice(1).map((band, i) => (
            <line
              key={`${band.id}-divider`}
              x1={slotLeft(i + 1)}
              y1={curveTop}
              x2={slotLeft(i + 1)}
              y2={baseline}
              stroke="var(--border)"
              strokeWidth={1}
              strokeDasharray="2 3"
            />
          ))}

          {/* Y-axis gridlines + headcount labels. */}
          {yTicks.map((tick) => {
            const y = yForAxis(tick);
            return (
              <g key={tick}>
                <line
                  x1={plotLeft}
                  y1={y}
                  x2={plotRight}
                  y2={y}
                  stroke="var(--border)"
                  strokeWidth={1}
                  opacity={0.6}
                />
                <text
                  x={plotLeft - 8}
                  y={y + 3}
                  textAnchor="end"
                  fontSize={8.5}
                  fill="var(--fg-subtle)"
                >
                  {Math.round(tick)}
                </text>
              </g>
            );
          })}
          {/* `rotate(angle, cx, cy)` pivots around (cx, cy) IN PLACE — unlike the bare `rotate(angle)`
              this replaced, which pivots around the SVG's global origin (0,0), throwing the text to
              wherever that rotation happens to land it (here, off in the tick-label column, reading
              as barely-visible clutter rather than an axis title). Pivoting on the text's own anchor
              keeps it centered on the plot's vertical midpoint the way a rotated axis title should be. */}
          <text
            x={12}
            y={(curveTop + baseline) / 2}
            transform={`rotate(-90, 12, ${(curveTop + baseline) / 2})`}
            textAnchor="middle"
            fontSize={8.5}
            fontWeight={600}
            fill="var(--fg-subtle)"
          >
            {yAxisLabel}
          </text>

          <line
            x1={plotLeft}
            y1={baseline}
            x2={plotRight}
            y2={baseline}
            stroke="var(--border-strong)"
            strokeWidth={1}
          />

          {curve.length > 1 && (
            <polyline
              points={curvePath}
              fill="none"
              stroke="var(--fg-subtle)"
              strokeWidth={1.5}
              strokeDasharray="4 3"
              style={{
                opacity: mounted ? 0.7 : 0,
                transition: `opacity ${DRAW_IN_DURATION} var(--ease)`,
              }}
            />
          )}

          {mean != null && curveXs.length > 0 && (
            <>
              <line
                x1={xForScore(mean)}
                y1={PADDING.top}
                x2={xForScore(mean)}
                y2={baseline}
                stroke="var(--accent)"
                strokeWidth={1}
                strokeDasharray="3 3"
                opacity={0.6}
              />
              <circle
                cx={xForScore(mean)}
                cy={PADDING.top}
                r={3}
                fill="var(--surface)"
                stroke="var(--accent)"
                strokeWidth={1.5}
              />
              <text
                x={xForScore(mean)}
                y={PADDING.top - 8}
                textAnchor="middle"
                fontSize={9}
                fontWeight={600}
                fill="var(--accent)"
              >
                {meanLabel}: {mean.toFixed(2)}
              </text>
            </>
          )}

          {bands.map((band, bandIndex) => {
            const cx = slotCenter(bandIndex);
            const dotsToShow = Math.min(band.count, maxDotsPerBand);
            const overflow = band.count - dotsToShow;
            const perRow = Math.max(1, Math.min(6, Math.ceil(Math.sqrt(dotsToShow || 1))));
            const isActive = hover.activeIndex === bandIndex;
            return (
              <g key={band.id}>
                {Array.from({ length: dotsToShow }).map((_, dotIndex) => {
                  const row = Math.floor(dotIndex / perRow);
                  const col = dotIndex % perRow;
                  const rowCount = Math.min(perRow, dotsToShow - row * perRow);
                  const rowOffset = (col - (rowCount - 1) / 2) * DOT_SPACING;
                  const x = cx + rowOffset;
                  const y = baseline - DOT_RADIUS - 2 - row * DOT_SPACING;
                  return (
                    <circle
                      key={dotIndex}
                      cx={x}
                      cy={y}
                      r={DOT_RADIUS}
                      fill={band.color}
                      stroke="var(--surface)"
                      strokeWidth={1}
                      style={{
                        opacity: mounted ? (hover.activeIndex == null || isActive ? 0.92 : 0.5) : 0,
                        transition: `opacity ${DRAW_IN_DURATION} var(--ease)`,
                        transitionDelay: `${(bandIndex * perRow + dotIndex) * 6}ms`,
                      }}
                    />
                  );
                })}
                {overflow > 0 && (
                  <text
                    x={cx}
                    y={baseline - DOT_RADIUS - 2 - Math.ceil(dotsToShow / perRow) * DOT_SPACING - 4}
                    textAnchor="middle"
                    fontSize={9}
                    fontWeight={600}
                    fill="var(--fg-muted)"
                  >
                    +{overflow}
                  </text>
                )}
                <text
                  x={cx}
                  y={height - PADDING.bottom + 16}
                  textAnchor="middle"
                  fontSize={10}
                  fontWeight={700}
                  fill={isActive ? band.color : 'var(--fg)'}
                >
                  {band.label}
                </text>
                {/* Transparent hit area across the whole slot — easier to aim for than the dot
                    cluster alone, and gives the tooltip a target even for a zero-count band. */}
                <rect
                  x={slotLeft(bandIndex)}
                  y={curveTop}
                  width={slotWidth}
                  height={baseline - curveTop}
                  fill="transparent"
                  onPointerEnter={() => hover.setActiveIndex(bandIndex)}
                  onPointerMove={() => hover.setActiveIndex(bandIndex)}
                />
              </g>
            );
          })}
        </svg>

        {activeBand && hover.activeIndex != null && (
          <ChartTooltip
            x={(slotCenter(hover.activeIndex) / VIEW_WIDTH) * 100}
            y={(curveTop / height) * 100 + 6}
          >
            <div className="font-semibold text-fg">{activeBand.label}</div>
            <div className="flex items-center gap-1.5">
              <span
                className="size-2 rounded-full"
                style={{ backgroundColor: activeBand.color }}
                aria-hidden="true"
              />
              {activeBand.count} {activeBand.count === 1 ? 'member' : 'members'} (
              {activeBand.percent.toFixed(1)}%)
            </div>
          </ChartTooltip>
        )}
      </div>

      {legend && (
        <div className="mt-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs">
          {bands.map((band) => (
            <div key={band.id} className="flex items-center gap-1.5 text-fg-subtle">
              <span
                className="inline-block size-2.5 rounded-full"
                style={{ backgroundColor: band.color }}
              />
              <span>
                {band.label} · <span className="font-semibold text-fg">{band.count}</span>{' '}
                {band.count === 1 ? 'Member' : 'Members'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
